<template>
  <div>
    <!-- En-tête : compteur, recherche, filtres et actions. -->
    <div class="px-3 pb-3">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <h5 class="mb-0">
          Subscribers <small class="text-muted">({{ filtered.length }} / {{ subscribers.length }})</small>
        </h5>
        <div class="d-flex align-items-center">
          <b-form-input
            v-model="searchQuery"
            size="sm"
            type="search"
            placeholder="Search email..."
            style="max-width: 240px;"
            class="mr-2"
          ></b-form-input>
          <b-button size="sm" variant="outline-primary" @click="openAdd"><feather type="plus" size="14" class="mr-1"></feather>Add subscriber</b-button>
        </div>
      </div>
      <!-- Filtres : date d'abonnement et hôte (domaine nu ou www). -->
      <div class="d-flex flex-wrap align-items-center">
        <label class="small text-muted mb-0 mr-1">From</label>
        <b-form-input v-model="dateFrom" size="sm" type="date" class="w-auto mr-3 mb-2 mb-md-0"></b-form-input>
        <label class="small text-muted mb-0 mr-1">To</label>
        <b-form-input v-model="dateTo" size="sm" type="date" class="w-auto mr-3 mb-2 mb-md-0"></b-form-input>
        <b-form-select v-model="hostFilter" :options="hostOptions" size="sm" class="w-auto mr-2 mb-2 mb-md-0"></b-form-select>
        <button
          type="button"
          class="btn btn-light btn-sm mr-2 mb-2 mb-md-0"
          v-if="hasActiveFilters"
          @click="clearFilters"
        ><feather type="x" size="14" class="mr-1"></feather>Clear</button>
        <button
          type="button"
          class="btn btn-outline-secondary btn-sm mb-2 mb-md-0 ml-auto"
          :disabled="!filtered.length"
          @click="exportCsv"
        ><feather type="download" size="14" class="mr-1"></feather>Export CSV</button>
      </div>
      <p class="text-danger mb-0 mt-2" v-if="actionError">{{ actionError }}</p>
    </div>

    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status"></div>
      <p class="text-muted mt-3 mb-0">Loading subscribers...</p>
    </div>
    <div v-else-if="loadError" class="text-center py-5">
      <p class="text-danger mb-0">{{ loadError }}</p>
    </div>
    <template v-else>
      <div class="table-responsive datatable-vue text-center">
        <b-table
          ref="TableSubscribers"
          show-empty
          striped
          hover
          head-variant="light"
          bordered
          stacked="md"
          :items="filtered"
          :fields="tablefields"
          :current-page="currentPage"
          :per-page="perPage"
        >
          <template #empty>
            <span>No subscribers for this store yet.</span>
          </template>
          <template #cell(email)="field">
            <a :href="'mailto:' + field.item.email">{{ field.item.email }}</a>
          </template>
          <template #cell(host)="field">{{ field.item.host || "—" }}</template>
          <template #cell(date)="field">{{ formatDate(field.item.date) }}</template>
          <template #cell(actions)="field">
            <button type="button" class="icon-btn icon-btn-danger" title="Delete subscriber" @click="confirmDelete(field.item)">
              <feather type="trash-2" size="16"></feather>
            </button>
          </template>
        </b-table>
      </div>
      <b-col md="12" class="my-1 p-0 pagination-justify" v-if="filtered.length > perPage">
        <b-pagination
          v-model="currentPage"
          :total-rows="filtered.length"
          :per-page="perPage"
          aria-controls="TableSubscribers"
          class="mt-4"
        ></b-pagination>
      </b-col>
    </template>

    <!-- Ajout manuel d'un abonné (mêmes champs que le formulaire de la
         vitrine : email + date d'abonnement). -->
    <b-modal ref="addModal" title="Add subscriber" @ok="addSubscriber" :ok-disabled="saving">
      <b-form @submit.prevent>
        <b-form-group label="Email" label-for="subscriber-email" class="mb-2">
          <b-form-input
            id="subscriber-email"
            v-model="newEmail"
            type="email"
            placeholder="name@example.com"
          ></b-form-input>
        </b-form-group>
        <p class="text-muted small mb-2">Added to this store ({{ site }}).</p>
        <p class="text-danger mb-0" v-if="addError">{{ addError }}</p>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import firebase from '@/utils/firestore-shim';
import { parseOrderDate, downloadCSV, inDateRange } from "../../../utils/store-stats";

export default {
  name: "table-subscribers",
  props: ["site"],
  data() {
    return {
      tablefields: [
        { key: "email", label: "Email", sortable: true },
        { key: "host", label: "Store", sortable: true },
        { key: "date", label: "Subscribed at", sortable: true },
        { key: "actions", label: "" }
      ],
      subscribers: [],
      searchQuery: '',
      dateFrom: '',
      dateTo: '',
      hostFilter: '',
      currentPage: 1,
      perPage: 50,
      loading: false,
      loadError: '',
      actionError: '',
      newEmail: '',
      addError: '',
      saving: false
    };
  },
  computed: {
    hostOptions() {
      return [
        { value: '', text: 'All hosts' },
        { value: this.site, text: this.site },
        { value: 'www.' + this.site, text: 'www.' + this.site }
      ];
    },
    hasActiveFilters() {
      return !!(this.searchQuery.trim() || this.dateFrom || this.dateTo || this.hostFilter);
    },
    // Filtrage local (les abonnés sont déjà tous chargés) : hôte, plage de
    // dates d'abonnement puis recherche textuelle sur l'email.
    filtered() {
      var q = this.searchQuery.trim().toLowerCase();
      return this.subscribers.filter((s) => {
        if (this.hostFilter && s.host !== this.hostFilter) return false;
        if ((this.dateFrom || this.dateTo) && !inDateRange(s.date, this.dateFrom, this.dateTo)) return false;
        if (q && (s.email || '').toLowerCase().indexOf(q) === -1) return false;
        return true;
      });
    }
  },
  watch: {
    searchQuery() {
      this.currentPage = 1;
    },
    dateFrom() {
      this.currentPage = 1;
    },
    dateTo() {
      this.currentPage = 1;
    },
    hostFilter() {
      this.currentPage = 1;
    }
  },
  mounted() {
    this.loadSubscribers();
  },
  methods: {
    // Date lisible d'un abonnement (les anciens abonnés n'en ont pas).
    formatDate(value) {
      if (!value) return "—";
      try {
        return value.toLocaleDateString();
      } catch (e) {
        return "—";
      }
    },
    // Remet tous les filtres à zéro (recherche incluse).
    clearFilters() {
      this.searchQuery = '';
      this.dateFrom = '';
      this.dateTo = '';
      this.hostFilter = '';
    },
    // Abonnés à la newsletter de la boutique : subscribers/{host}/email.
    // L'abonnement peut avoir été fait depuis le domaine nu ou le
    // sous-domaine www : on lit les deux chemins et on déduplique par email.
    async loadSubscribers() {
      this.loading = true;
      this.loadError = '';
      try {
        var seen = {};
        var list = [];
        var hosts = [this.site, 'www.' + this.site];
        for (var i = 0; i < hosts.length; i++) {
          await firebase.firestore().collection('subscribers/' + hosts[i] + '/email')
            .get()
            .then((snapshot) => {
              snapshot.forEach((doc) => {
                var data = doc.data() || {};
                var email = String(data.email || doc.id || '').trim().toLowerCase();
                if (!email || seen[email]) return;
                seen[email] = true;
                list.push({
                  email: email,
                  host: hosts[i],
                  date: parseOrderDate(data.date || data.created || data.createdAt)
                });
              });
            });
        }
        // Tri : abonnements les plus récents en premier, sans date en fin.
        this.subscribers = list.sort((a, b) => {
          var ta = a.date ? a.date.getTime() : 0;
          var tb = b.date ? b.date.getTime() : 0;
          if (ta !== tb) return tb - ta;
          return (a.email || '').localeCompare(b.email || '');
        });
      } catch (error) {
        this.loadError = 'Unable to load subscribers (' + ((error && error.message) || error) + ').';
      } finally {
        this.loading = false;
      }
    },
    openAdd() {
      this.newEmail = '';
      this.addError = '';
      this.$refs.addModal.show();
    },
    // Ajout manuel : même forme de document que la vitrine (id = email).
    // Le bouton OK de la modale reste ouverte tant que l'email est invalide
    // ou déjà présent (preventDefault sur l'événement de la modale).
    async addSubscriber(event) {
      var email = this.newEmail.trim().toLowerCase();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        this.addError = 'Enter a valid email address.';
        if (event && event.preventDefault) event.preventDefault();
        return;
      }
      this.saving = true;
      this.addError = '';
      try {
        var ref = firebase.firestore().collection('subscribers/' + this.site + '/email').doc(email);
        if ((await ref.get()).exists) {
          this.addError = 'This subscriber already exists.';
          if (event && event.preventDefault) event.preventDefault();
          return;
        }
        await ref.set({ email: email, date: new Date().toISOString() });
        this.subscribers.unshift({ email: email, host: this.site, date: new Date() });
      } catch (error) {
        this.addError = 'Unable to add subscriber (' + ((error && error.message) || error) + ').';
        if (event && event.preventDefault) event.preventDefault();
      } finally {
        this.saving = false;
      }
    },
    // Suppression avec confirmation : l'email est retiré des deux chemins
    // (domaine nu et www) pour désabonner complètement l'adresse.
    confirmDelete(subscriber) {
      this.$bvModal.msgBoxConfirm('Delete subscriber ' + subscriber.email + ' ?', {
        title: 'Confirmation',
        size: 'md',
        buttonSize: 'sm',
        okVariant: 'danger',
        okTitle: 'DELETE',
        cancelTitle: 'CANCEL',
        footerClass: 'p-2',
        hideHeaderClose: false
      })
        .then((value) => {
          if (value) this.deleteSubscriber(subscriber);
        })
        .catch(() => {});
    },
    async deleteSubscriber(subscriber) {
      this.actionError = '';
      try {
        var hosts = [this.site, 'www.' + this.site];
        for (var i = 0; i < hosts.length; i++) {
          await firebase.firestore().collection('subscribers/' + hosts[i] + '/email').doc(subscriber.email).delete();
        }
        this.subscribers = this.subscribers.filter((s) => s.email !== subscriber.email);
      } catch (error) {
        this.actionError = 'Unable to delete subscriber (' + ((error && error.message) || error) + ').';
      }
    },
    // Export CSV des lignes filtrées (listes d'emailing).
    exportCsv() {
      var rows = this.filtered.map((s) => ({
        Email: s.email,
        Store: s.host,
        'Subscribed at': s.date ? s.date.toLocaleDateString() : ''
      }));
      downloadCSV(rows, 'subscribers-' + this.site + '-' + new Date().toISOString().slice(0, 10) + '.csv');
    }
  }
};
</script>

<style></style>
